/** tm/components/GovFeedBulkDialog.tsx — 선택 일괄 등록 확인 · 진행 · 결과 (2026-09-30)
 *  GovPage 등록 모달과 같은 틀(어두운 머리 · R 16 · 하단 [취소][실행]).
 */
import { useMemo, useState } from 'react';
import { C, T, R, PBar } from './ui';
import { AMBER, programOf } from './GovFeedParts';
import type { GovFeedRow } from '../types';

export interface BulkOutcome { id: string; ok: boolean; error?: string }

interface Props {
  rows: GovFeedRow[];
  onClose: () => void;
  /** 등록 실행 — 진행률 콜백, 건별 결과 반환 */
  run: (ids: string[], onProgress: (done: number, total: number) => void) => Promise<BulkOutcome[]>;
}

const stat = (label: string, value: number, color: string = C.text): React.ReactNode => (
  <div style={{ flex: 1, padding: '10px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.bgAlt }}>
    <div style={{ fontSize: T.xs, color: C.sub }}>{label}</div>
    <div style={{ fontSize: T.xl, fontWeight: 700, color, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value.toLocaleString()}</div>
  </div>
);

export default function GovFeedBulkDialog({ rows, onClose, run }: Props) {
  const pending = useMemo(() => rows.filter(r => r.aiStatus !== 'done'), [rows]);
  const [skipPending, setSkipPending] = useState(true);
  const target = useMemo(() => (skipPending ? rows.filter(r => r.aiStatus === 'done') : rows), [rows, skipPending]);
  const upd = target.filter(r => r.dupProgramId).length;
  const unsure = target.filter(r => (r.unsureCount ?? 0) > 0).length;
  const unfit = target.filter(r => r.screen === 'unfit').length;
  const [phase, setPhase] = useState<'confirm' | 'running' | 'done'>('confirm');
  const [prog, setProg] = useState({ done: 0, total: 0 });
  const [out, setOut] = useState<BulkOutcome[]>([]);
  const byId = useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);

  async function go() {
    if (!target.length) return;
    setPhase('running');
    setProg({ done: 0, total: target.length });
    const res = await run(target.map(r => r.id), (done, total) => setProg({ done, total }));
    setOut(res);
    setPhase('done');
  }

  const okN = out.filter(o => o.ok).length;
  const fails = out.filter(o => !o.ok);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={phase === 'running' ? undefined : onClose} />
      <div role="dialog" aria-modal="true" style={{ position: 'relative', width: '100%', maxWidth: 480, maxHeight: '92vh', borderRadius: R.lg, background: C.white, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #111827, #1B3A5C)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: T.lg, fontWeight: 700, color: '#fff' }}>{phase === 'done' ? '일괄 등록 결과' : '선택 일괄 등록'}</span>
          {phase !== 'running' && <button onClick={onClose} aria-label="닫기" style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', color: '#fff', fontSize: T.md }}>✕</button>}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {phase === 'confirm' && (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                {stat('등록', target.length, C.accent)}
                {stat('새로 등록', target.length - upd)}
                {stat('기존 공고 갱신', upd)}
              </div>
              <div style={{ fontSize: T.sm, color: C.sub, lineHeight: 1.6, marginTop: 14 }}>
                표에서 고친 값(수정됨)이 있으면 그 값으로, 없으면 AI가 채운 값으로 등록합니다. 이미 등록된 공고는 그 공고를 갱신합니다.
              </div>
              {(pending.length > 0 || unsure > 0 || unfit > 0) && (
                <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: R.md, background: AMBER.bg, border: `1px solid ${AMBER.border}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {pending.length > 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: T.sm, color: AMBER.text, cursor: 'pointer' }}>
                      <input type="checkbox" checked={skipPending} onChange={e => setSkipPending(e.target.checked)} style={{ accentColor: C.accent }} />
                      AI 분석 전 <b>{pending.length.toLocaleString()}건</b>은 빼고 등록
                    </label>
                  )}
                  {unsure > 0 && <div style={{ fontSize: T.sm, color: AMBER.text }}>확인 필요 칸이 남은 공고 <b>{unsure.toLocaleString()}건</b> 포함</div>}
                  {unfit > 0 && <div style={{ fontSize: T.sm, color: AMBER.text }}>AI 비추천 <b>{unfit.toLocaleString()}건</b> 포함</div>}
                </div>
              )}
            </>
          )}

          {phase === 'running' && (
            <div style={{ padding: '12px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.sm, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                <span>등록 중…</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: C.accent }}>{prog.done.toLocaleString()} / {prog.total.toLocaleString()}</span>
              </div>
              <PBar value={prog.total ? (prog.done / prog.total) * 100 : 0} h={6} />
              <div style={{ fontSize: T.xs, color: C.muted, marginTop: 8 }}>창을 닫지 말고 잠시 기다려 주세요</div>
            </div>
          )}

          {phase === 'done' && (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                {stat('등록 완료', okN, C.green)}
                {stat('실패', fails.length, fails.length ? C.red : C.muted)}
              </div>
              {fails.length > 0 && (
                <>
                  <div style={{ fontSize: T.sm, color: C.sub, marginTop: 14, marginBottom: 6 }}>실패한 공고는 표에 남겨 두고 사유를 표시했습니다.</div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, maxHeight: 220, overflowY: 'auto' }}>
                    {fails.map((f, i) => {
                      const r = byId.get(f.id);
                      return (
                        <div key={f.id} style={{ padding: '8px 12px', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
                          <div style={{ fontSize: T.sm, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r ? programOf(r).name : f.id}</div>
                          <div style={{ fontSize: T.xs, color: C.red, marginTop: 2 }}>{f.error || '알 수 없는 오류'}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
          {phase === 'confirm' && (
            <>
              <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>취소</button>
              <button onClick={go} disabled={!target.length} style={{ flex: 2, padding: '10px 0', borderRadius: R.md, border: 'none', background: `linear-gradient(135deg,${C.accent},${C.primary})`, color: '#fff', fontSize: T.md, fontWeight: 700, cursor: target.length ? 'pointer' : 'default', opacity: target.length ? 1 : 0.5 }}>
                {target.length.toLocaleString()}건 등록
              </button>
            </>
          )}
          {phase === 'done' && (
            <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: R.md, border: 'none', background: C.accent, color: '#fff', fontSize: T.md, fontWeight: 700, cursor: 'pointer' }}>확인</button>
          )}
          {phase === 'running' && <div style={{ flex: 1, height: 38 }} />}
        </div>
      </div>
    </div>
  );
}
